/**
 * Filters an object by removing entries with undefined, null, or empty string values.
 *
 * @param obj - The input object to be filtered.
 * @returns A new object with only non-empty values.
 * @template T - The type of the input object.
 */
export function getNonEmptyValuesFromObject<T>(obj: T): T {
  if (!obj) return {} as T;
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as T;
}

/**
 * Checks if the value of a given key in an object has changed.
 *
 * @param {string} key - The key to check for in the object.
 * @param {string | number | undefined} value - The value to compare with the object's key value.
 * @param {Record<string, unknown> | null | undefined} object - The object to check in.
 * @returns {boolean} - Returns true if the value of the key in the object has changed or if the key does not exist in the object, false otherwise.
 */
export const isParameterValueChanged = (
  key: string,
  value: string | number | undefined,
  object: Record<string, unknown>
): boolean => {
  if (!object || object[key] === undefined) {
    return false;
  }
  return object[key] !== value;
};

/**
 * Removes a set of keys from a given object.
 *
 * @template T - The type of the input object.
 * @param {string[]} keysToRemove - The array of keys to be removed from the object.
 * @param {T} inputObject - The input object from which keys will be removed.
 * @returns {T} - A new object of the same type as the input object, but without the keys specified in keysToRemove.
 */
export const removeKeysFromObject = <T extends Record<string, unknown>>(
  keysToRemove: (keyof T)[],
  inputObject: T
): T => {
  if (!inputObject) {
    return {} as T;
  }
  return Object.keys(inputObject).reduce((result: Partial<T>, key: string) => {
    if (!keysToRemove.includes(key as keyof T)) {
      result[key as keyof T] = inputObject[key as keyof T];
    }
    return result;
  }, {} as Partial<T>) as T;
};

const identity = (x: string) => x;

interface ParseOptions {
  keyValueSeparator: string;
  paramsSeparator: string;
  parseKey?: (key: string) => string;
}

/**
 * Parses a string into a key-value pair object.
 *
 * @param {string} string - The string to be parsed.
 * @param {Object} options - An object containing the separators and a key parsing function.
 * @param {string} options.keyValueSeparator - The separator between keys and values in the string.
 * @param {string} options.paramsSeparator - The separator between different key-value pairs in the string.
 * @param {Function} [options.parseKey=identity] - An optional function to parse the keys. Defaults to an identity function.
 * @returns {Record<string, string>} - An object with key-value pairs parsed from the input string.
 */
export const parseStringToParams = (
  string: string,
  { keyValueSeparator, paramsSeparator, parseKey = identity }: ParseOptions
): Record<string, string> =>
  string.split(paramsSeparator).reduce((obj, str) => {
    const [key, value] = str.split(keyValueSeparator);
    if (key && value) {
      obj[parseKey(key)] = value;
    }
    return obj;
  }, {});

/**
 * Reverses the key-value pairs in a given dictionary.
 *
 * @template D - The type of the input dictionary.
 * @template S - The type of the initial object.
 * @param {D} dictionary - The input dictionary whose key-value pairs are to be reversed.
 * @param {Partial<S>} initialObject - An optional initial object to which the reversed key-value pairs will be added. Defaults to an empty object.
 * @returns {Partial<S>} - An object with the reversed key-value pairs from the input dictionary. If an initial object was provided, the reversed key-value pairs will be added to it.
 */
export function reverseDictionary<D, S>(
  dictionary: D,
  initialObject: Partial<S> = {}
): Partial<S> {
  return Object.keys(dictionary).reduce((result, key) => {
    const value = dictionary[key];
    if (value) {
      result[value] = key;
    }
    return result;
  }, initialObject);
}
